<template>
  <div
    v-if="isSubMenu"
    class="menu-wrapper"
    :class="{
      'auto-width': autoWidth,
      'size-l': size === 'l',
      'size-xl': size === 'xl',
      'size-xxl': size === 'xxl',
      'size-3xl': size === '3xl'
    }"
    ref="menu"
    v-on="$listeners"
    @click="onClick"
  >
    <slot />
  </div>
  <div
    v-else
    class="menu-wrapper"
    :class="{
      'auto-width': autoWidth,
      'size-l': size === 'l',
      'size-xl': size === 'xl',
      'size-xxl': size === 'xxl',
      'size-3xl': size === '3xl'
    }"
    ref="menu"
    v-on="$listeners"
    @click="onClick"
    v-non-render
  >
    <slot />
  </div>
</template>

<script lang="ts">
import tippy, { Instance } from 'tippy.js'
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import { mapStores } from 'pinia'
import { useMenuStore, MenuStore } from '@/modules/global/stores/MenuStore'

@Component({
  computed: mapStores(useMenuStore)
})
export default class NMenu extends Vue {
  @Prop({ default: false, type: Boolean }) isSubMenu!: boolean
  @Prop({ default: false, type: Boolean }) hideOnClick!: boolean
  @Prop({ default: false, type: Boolean }) autoWidth!: boolean
  @Prop({ default: () => {} }) tippyProps!: any
  @Prop({
    type: String,
    validator: (prop: string) => ['m', 'l', 'xl', 'xxl', '3xl'].includes(prop),
    default: 'm'
  })
  size?: String
  @Ref('menu') menuRef!: HTMLElement
  currentTippyInstance: Instance<any> | null = null
  menuStore!: MenuStore

  /** `referenceRect` overrides the anchor box, e.g. a zero-size rect at the cursor for a context menu. */
  open(openFrom: HTMLElement, referenceRect?: () => DOMRect) {
    const openBefore = this.menuStore.currentlyOpenElementRef
    this.menuStore.closeActiveMenu()
    if (openBefore === openFrom) {
      return
    }

    this.menuStore.setCurrentMenuData(this, openFrom)
    // Each open() built a fresh tippy and only ever hid the previous one, leaking a hidden popper
    // into <body> per open. Silence the outgoing instance's hooks first: destroy() fires onHidden
    // synchronously, which would emit changeVisibility(false) for the menu we are opening and let
    // listeners tear down the state they just set up.
    const previous = this.currentTippyInstance
    this.currentTippyInstance = null
    if (previous) {
      previous.setProps({ onShow: () => {}, onHidden: () => {} })
      previous.destroy()
    }
    this.currentTippyInstance = tippy('body', {
      appendTo: () => document.body,
      content: this.menuRef,
      showOnCreate: true,
      interactive: true,
      hideOnClick: true,
      // The tippy reference is <body> (position comes from getReferenceClientRect below), so
      // without this tippy reads every press as a press on its own reference and hideOnClick never
      // fires for touch input - the menu stayed open on tap-outside. Point the press detection at
      // the element the menu is anchored to instead.
      triggerTarget: openFrom,
      theme: 'none',
      trigger: 'manual',
      placement: 'bottom-start',
      getReferenceClientRect: referenceRect ?? (() => openFrom.getBoundingClientRect()),
      onHidden: () => {
        this.menuStore.setCurrentMenuData(null, null)
        this.$emit('changeVisibility', false)
      },
      onShow: () => {
        this.menuStore.setCurrentMenuData(this, openFrom)
        this.$emit('changeVisibility', true)
      },
      ...this.tippyProps
    })[0]
  }

  close() {
    this.menuStore.setCurrentMenuData(null, null)
    this.currentTippyInstance?.hide()
  }

  beforeDestroy() {
    // The popper is appended to <body>, so it outlives the component that owns it: a menu left
    // open when its anchor unmounts (a v-if'd sidebar, a closed modal) kept floating over the app.
    const instance = this.currentTippyInstance
    this.currentTippyInstance = null
    if (!instance) {
      return
    }
    // destroy() invokes onHidden synchronously; run our own teardown instead of emitting from a
    // component that is going away, and only clear the store if this menu is the one it points at.
    instance.setProps({ onShow: () => {}, onHidden: () => {} })
    instance.destroy()
    if (this.menuStore.currentMenuInstance === this) {
      this.menuStore.setCurrentMenuData(null, null)
    }
  }

  isFocused() {
    const isTippyBox = document.activeElement?.classList.contains('tippy-box')
    const isTippyBoxFocused = isTippyBox && this.isChildElementOf(this.menuRef, document.activeElement)
    const isChildOfMenuFocused = this.isChildElementOf(document.activeElement, this.menuRef)

    return isTippyBoxFocused || isChildOfMenuFocused
  }

  isOpen() {
    return this.currentTippyInstance?.state?.isShown || false
  }

  /** Unlike isOpen, already true while the menu fades in (tippy sets isShown only once the transition ends) */
  isVisible() {
    return this.currentTippyInstance?.state?.isVisible || false
  }

  onClick(event: MouseEvent) {
    if (this.hideOnClick) {
      this.close()
    }
    this.$emit('click', event)
  }

  isChildElementOf(element: Element | null, parent: Element | null) {
    if (!parent) return false
    else if (parent === element) return true
    else {
      for (let i = 0; i < parent.children.length; i++) {
        if (this.isChildElementOf(element, parent.children.item(i))) {
          return true
        }
      }
    }
    return false
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.menu-wrapper {
  @include notch-style-floating;
  background-color: $notch-color-neutral-grey-000;
  z-index: $notch-layer-1;
  border-radius: $notch-radius-2x;
  padding: $notch-space-2x;

  &:not(.auto-width) {
    width: $notch-space-60x;
  }

  &.size-l {
    width: $notch-space-64x;
  }

  &.size-xl {
    width: $notch-space-75x;
  }

  &.size-xxl {
    width: $notch-space-90x;
  }

  &.size-3xl {
    width: $notch-space-95x;
  }
}
</style>
